import { Button, ButtonProps, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle } from '@mui/material'
import { FormEvent, FormEventHandler, memo, MouseEventHandler, ReactNode, type JSX } from 'react'
import { useTranslation } from 'react-i18next'

export interface FormDialogActionsProps {
  onCancel: MouseEventHandler<HTMLButtonElement>
  children?: ReactNode
  confirmLabel?: string
  confirmColor?: ButtonProps['color']
  disabled?: boolean
  loading?: boolean
}

const FormDialogActions = ({ onCancel, confirmLabel, confirmColor, disabled, loading }: FormDialogActionsProps) => {
  const { t } = useTranslation()
  return (
    <DialogActions>
      <Button disabled={loading} color="inherit" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" disabled={disabled} loading={loading} color={confirmColor}>
        {confirmLabel ?? t('common.confirm')}
      </Button>
    </DialogActions>
  )
}

export interface FormDialogProps extends Omit<DialogProps, 'title' | 'component' | 'slotProps' | 'onSubmit'> {
  onConfirm: (event: FormEvent<HTMLFormElement>) => void
  title: ReactNode
  confirmLabel?: string
  confirmColor?: ButtonProps['color']
  disabled?: boolean
  loading?: boolean
  dividers?: boolean
  disableGutterContent?: boolean
}

const FormDialog = ({
  onClose,
  onConfirm,
  title,
  children,
  open,
  confirmLabel,
  confirmColor = 'primary',
  disabled = false,
  loading = false,
  dividers = false,
  disableGutterContent = false,
  ...dialogProps
}: FormDialogProps): JSX.Element => {
  const handleClose: DialogProps['onClose'] = (e, reason) => {
    if (!loading && onClose) onClose(e, reason)
  }

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!loading) onConfirm(e)
  }

  return (
    <Dialog
      {...dialogProps}
      onClose={handleClose}
      open={open}
      slotProps={{ paper: { component: 'form', onSubmit: handleSubmit } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers={dividers} style={{ padding: disableGutterContent ? 0 : undefined }}>
        {children}
      </DialogContent>
      <FormDialogActions
        onCancel={(e) => handleClose(e, 'escapeKeyDown')}
        confirmLabel={confirmLabel}
        confirmColor={confirmColor}
        disabled={disabled}
        loading={loading}
      />
    </Dialog>
  )
}

export default memo(FormDialog)
